import SmartBooking from '@/pages/SmartBooking';
import CustomerPortal from '@/pages/CustomerPortal';
import { CustomerPortalNotifications } from '@/components/CustomerPortalRequest';
import {LiquidGoldProvider} from '@/components/mobile/LiquidGoldContext';
import CallRecovery from '@/components/CallRecovery';
import GlobalDesktopDock from '@/components/hd/GlobalDesktopDock';
import MobileDesign from '@/components/mobile/MobileDesign';
import { lazy, Suspense } from 'react';
import LockedAreaDialog from '@/components/LockedAreaDialog';
import { homeFor } from '@/lib/navigation';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ShellStatusProvider } from "@/context/ShellStatusContext";
import { Toaster } from "@/components/ui/sonner";
import { EmployeeShell, AdminShell, TeamLeaderShell } from "@/components/Layout";
import DesignLoading from "@/components/hd/DesignLoading";
import { loadCallMode } from "@/lib/routePreload";

import { GamificationProvider } from "@/components/Gamification";
const EmailCampaigns=lazy(()=>import('@/pages/employee/EmailCampaigns'));
const MailFollowups=lazy(()=>import('@/pages/employee/MailFollowups'));
const CampaignUnsubscribe=lazy(()=>import('@/pages/CampaignUnsubscribe'));
const Login=lazy(()=>import('@/pages/Login'));
const SceneReview=lazy(()=>import('@/pages/SceneReview'));
const Notifications=lazy(()=>import('@/pages/Notifications'));
const Career=lazy(()=>import('@/pages/employee/Career'));
const GamificationAdmin=lazy(()=>import('@/pages/admin/GamificationAdmin'));
const Home=lazy(()=>import('@/pages/employee/Home'));
const CallMode=lazy(loadCallMode);
const Opportunities=lazy(()=>import('@/pages/employee/Opportunities'));
const OpportunityDetail=lazy(()=>import('@/pages/employee/OpportunityDetail'));
const Ranking=lazy(()=>import('@/pages/employee/Ranking'));
const Profile=lazy(()=>import('@/pages/employee/Profile'));
const Followups=lazy(()=>import('@/pages/employee/Followups'));
const Mailing=lazy(()=>import('@/pages/employee/Mailing'));
const Callbacks=lazy(()=>import('@/pages/employee/Callbacks'));
const Referrals=lazy(()=>import('@/pages/employee/Referrals'));
const ReferralNetwork=lazy(()=>import('@/pages/employee/ReferralNetwork'));
const Promo=lazy(()=>import('@/pages/employee/Promo'));
const PromoCard=lazy(()=>import('@/pages/employee/PromoCard'));
const Contacted=lazy(()=>import('@/pages/employee/Contacted'));
const MyContacts=lazy(()=>import('@/pages/employee/MyContacts'));
const AssignedLeads=lazy(()=>import('@/pages/employee/AssignedLeads'));
const SmartMode=lazy(()=>import('@/pages/employee/SmartMode'));
const Tasks=lazy(()=>import('@/pages/employee/Tasks'));
const WhatsAppInbox=lazy(()=>import('@/pages/employee/WhatsAppInbox'));
const WhatsAppConnection=lazy(()=>import('@/pages/employee/WhatsAppConnection'));
const Appointments=lazy(()=>import('@/pages/employee/Appointments'));
const Analytics=lazy(()=>import('@/pages/Analytics'));
const Promoter=lazy(()=>import('@/pages/promoter/Promoter'));
const Dashboard=lazy(()=>import('@/pages/admin/Dashboard'));
const LiveTeam=lazy(()=>import('@/pages/admin/LiveTeam'));
const Leads=lazy(()=>import('@/pages/admin/Leads'));
const Routing=lazy(()=>import('@/pages/admin/Routing'));
const AdminOpportunities=lazy(()=>import('@/pages/admin/AdminOpportunities'));
const TeamOpportunities=lazy(()=>import('@/pages/team/TeamOpportunities'));
const TeamContracts=lazy(()=>import('@/pages/team/TeamContracts'));
const PromoterAssignments=lazy(()=>import('@/pages/team/PromoterAssignments'));
const Pipeline=lazy(()=>import('@/pages/admin/Pipeline'));
const Customers=lazy(()=>import('@/pages/admin/Customers'));
const AssignedContacts=lazy(()=>import('@/pages/admin/AssignedContacts'));
const CustomerDetail=lazy(()=>import('@/pages/admin/CustomerDetail'));
const Mails=lazy(()=>import('@/pages/admin/Mails'));
const Employees=lazy(()=>import('@/pages/admin/Employees'));
const Teams=lazy(()=>import('@/pages/admin/Teams'));
const Import=lazy(()=>import('@/pages/admin/Import'));
const HistoricalImport=lazy(()=>import('@/pages/admin/HistoricalImport'));
const Research=lazy(()=>import('@/pages/admin/Research'));
const Duplicates=lazy(()=>import('@/pages/admin/Duplicates'));
const Settings=lazy(()=>import('@/pages/admin/Settings'));
const Guides=lazy(()=>import('@/pages/admin/Guides'));
const AdminContacted=lazy(()=>import('@/pages/admin/AdminContacted'));
const ChancenStats=lazy(()=>import('@/pages/admin/ChancenStats'));
const AuditLog=lazy(()=>import('@/pages/admin/AuditLog'));
const LoginLog=lazy(()=>import('@/pages/admin/LoginLog'));
const WhatsAppConfiguration=lazy(()=>import('@/pages/admin/WhatsAppConfiguration'));

function Guard({ roles, emails, children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <DesignLoading/>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  if (user?.system_account === "scene_reviewer" && loc.pathname !== "/scene-review") {
    return <Navigate to="/scene-review" replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={homeFor(user)} replace />;
  }
  if (emails && !emails.includes((user.email || "").trim().toLowerCase())) {
    return <Navigate to={homeFor(user)} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <DesignLoading/>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
}

const Emp = ({ children }) => <Guard roles={["mitarbeiter", "teamleiter", "admin", "vertriebspartner"]}><EmployeeShell>{children}</EmployeeShell></Guard>;
const Adm = ({ children }) => <Guard roles={["admin"]}><AdminShell>{children}</AdminShell></Guard>;
const AdminEmployeeLog = ({ children }) => <Guard roles={["admin"]}><EmployeeShell>{children}</EmployeeShell></Guard>;
const AdminLog = ({ children }) => <Guard roles={["admin"]}><AdminShell>{children}</AdminShell></Guard>;
const Team = ({ children }) => <Guard roles={["teamleiter"]}><TeamLeaderShell>{children}</TeamLeaderShell></Guard>;
const InterestingContacts = lazy(() => import("@/pages/employee/InterestingContacts"));
const Prm = ({ children }) => <Guard roles={["promoter"]}>{children}</Guard>;
const Review = ({ children }) => {
  const {user,ready}=useAuth();
  const loc=useLocation();
  if(!ready)return <DesignLoading/>;
  if(!user)return <Navigate to="/login" replace state={{from:loc}}/>;
  if(user?.permissions?.can_review_scenes!==true&&user?.role!=="admin")return <Navigate to={homeFor(user)} replace/>;
  return children;
};

function App() {
  if (window.location.pathname === "/beratung") return <SmartBooking />;
  if (window.location.pathname === "/kundenportal" || window.location.pathname.startsWith("/kundenportal/")) return <CustomerPortal />;
  return (
    <AuthProvider>
      <BrowserRouter><LiquidGoldProvider><MobileDesign/>

        <LockedAreaDialog />
        <ShellStatusProvider><GamificationProvider>
        <Toaster position="top-center" richColors theme="dark" /><CustomerPortalNotifications/>
        <Suspense fallback={<DesignLoading/>}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/campaign-unsubscribe" element={<CampaignUnsubscribe />} />
          <Route path="/m/email-campaigns" element={<Guard emails={["jan.nesic@loewen-stark.net"]}><Emp><EmailCampaigns /></Emp></Guard>} />
          <Route path="/m/smart" element={<Emp><SmartMode /></Emp>} />
          <Route path="/login" element={<Login />} />
          <Route path="/scene-review" element={<Review><SceneReview /></Review>} />

          <Route path="/m/career" element={<Emp><Career /></Emp>} />
          <Route path="/m/audit-log" element={<AdminEmployeeLog><AuditLog /></AdminEmployeeLog>} />
          <Route path="/m/login-log" element={<AdminEmployeeLog><LoginLog /></AdminEmployeeLog>} />
          <Route path="/admin/gamification" element={<Adm><GamificationAdmin /></Adm>} />
          <Route path="/m/minijob-contacts" element={<Guard roles={["teamleiter", "admin"]}><Emp><Contacted minijobOnly /></Emp></Guard>} />
          <Route path="/m/home" element={<Emp><Home /></Emp>} />
          <Route path="/m/insights" element={<Navigate to="/m/home" replace />} />
          <Route path="/m/prewarm-call" element={<Guard emails={["jan.nesic@loewen-stark.net", "philip.bogdan@loewen-stark.net"]}><Emp><CallRecovery><CallMode prewarm /></CallRecovery></Emp></Guard>} />
          <Route path="/m/call" element={<Emp><CallRecovery><CallMode /></CallRecovery></Emp>} />
          <Route path="/m/promo" element={<Emp><Promo /></Emp>} />
          <Route path="/m/promo/:id" element={<Emp><PromoCard /></Emp>} />
          <Route path="/promoter/interesting-contacts" element={<Prm><InterestingContacts /></Prm>} />
          <Route path="/promoter" element={<Prm><Promoter /></Prm>} />
          <Route path="/m/opportunities" element={<Emp><Opportunities /></Emp>} />
          <Route path="/m/opportunities/:id" element={<Emp><OpportunityDetail /></Emp>} />
          <Route path="/m/performance" element={<Emp><Analytics /></Emp>} />
          <Route path="/m/ranking" element={<Emp><Ranking /></Emp>} />
          <Route path="/m/email" element={<Navigate to="/m/mail-followups" replace />} />
          <Route path="/m/mail-followups" element={<Emp><MailFollowups /></Emp>} />
          <Route path="/m/whatsapp" element={<Emp><WhatsAppInbox /></Emp>} />
          <Route path="/m/whatsapp-connection" element={<Emp><WhatsAppConnection /></Emp>} />
          <Route path="/m/profile" element={<Emp><Profile /></Emp>} />
          <Route path="/m/notifications" element={<Emp><Notifications /></Emp>} />
          <Route path="/m/followups" element={<Emp><Followups /></Emp>} />
          <Route path="/m/mailing" element={<Emp><Mailing /></Emp>} />
          <Route path="/m/networking" element={<Navigate to="/m/my-contacts?tab=private" replace />} />
          <Route path="/m/callbacks" element={<Emp><Callbacks /></Emp>} />
          <Route path="/m/referrals" element={<Emp><Referrals /></Emp>} />
          <Route path="/m/contacted" element={<Emp><Contacted /></Emp>} />
          <Route path="/m/my-contacts" element={<Emp><MyContacts /></Emp>} />
          <Route path="/m/leads" element={<Emp><AssignedLeads /></Emp>} />
          <Route path="/m/referrals/network" element={<Emp><ReferralNetwork /></Emp>} />
          <Route path="/m/appointments" element={<Emp><Appointments /></Emp>} />
          <Route path="/m/more" element={<Navigate to="/m/tasks" replace />} />
          <Route path="/m/interesting-contacts" element={<Emp><InterestingContacts /></Emp>} />
          <Route path="/m/tasks" element={<Emp><Tasks /></Emp>} />
          <Route path="/m/contact/:contactId" element={<Emp><CallRecovery><CallMode /></CallRecovery></Emp>} />

          <Route path="/team/cockpit" element={<Team><Dashboard /></Team>} />
          <Route path="/team/opportunities" element={<Team><TeamOpportunities /></Team>} />
          <Route path="/team/contracts" element={<Team><TeamContracts /></Team>} />
          <Route path="/team/promoter-assignments" element={<Team><PromoterAssignments /></Team>} />
          <Route path="/team/contacts" element={<Team><AdminContacted /></Team>} />
          <Route path="/team/assigned" element={<Team><AssignedContacts /></Team>} />
          <Route path="/team/customers" element={<Team><Customers /></Team>} />
          <Route path="/team/members" element={<Team><LiveTeam /></Team>} />
          <Route path="/team/customers/:id" element={<Team><CustomerDetail /></Team>} />

          <Route path="/admin/whatsapp" element={<Adm><WhatsAppConfiguration /></Adm>} />
          <Route path="/admin/dashboard" element={<Adm><Dashboard /></Adm>} />
          <Route path="/admin/live-team" element={<Adm><LiveTeam /></Adm>} />
          <Route path="/admin/leads" element={<Adm><Leads /></Adm>} />
          <Route path="/admin/assigned" element={<Adm><AssignedContacts /></Adm>} />
          <Route path="/admin/routing" element={<Adm><Routing /></Adm>} />
          <Route path="/admin/opportunities" element={<Adm><AdminOpportunities /></Adm>} />
          <Route path="/admin/pipeline" element={<Adm><Pipeline /></Adm>} />
          <Route path="/admin/customers" element={<Adm><Customers /></Adm>} />
          <Route path="/admin/contacted" element={<Adm><AdminContacted /></Adm>} />
          <Route path="/admin/chancen-statistik" element={<Adm><ChancenStats /></Adm>} />
          <Route path="/admin/customers/:id" element={<Adm><CustomerDetail /></Adm>} />
          <Route path="/admin/duplicates" element={<Adm><Duplicates /></Adm>} />
          <Route path="/admin/mails" element={<Adm><Mails /></Adm>} />
          <Route path="/admin/employees" element={<Adm><Employees /></Adm>} />
          <Route path="/admin/teams" element={<Adm><Teams /></Adm>} />
          <Route path="/admin/analytics" element={<Adm><Analytics /></Adm>} />
          <Route path="/admin/ranking" element={<Adm><Ranking /></Adm>} />
          <Route path="/admin/import" element={<Adm><Import /></Adm>} />
          <Route path="/admin/historical-import" element={<Adm><HistoricalImport /></Adm>} />
          <Route path="/admin/research" element={<Adm><Research /></Adm>} />
          <Route path="/admin/notifications" element={<Adm><Notifications /></Adm>} />
          <Route path="/admin/settings" element={<Adm><Settings /></Adm>} />
          <Route path="/admin/guides" element={<Adm><Guides /></Adm>} />
          <Route path="/admin/audit-log" element={<Adm><AuditLog /></Adm>} />
          <Route path="/admin/login-log" element={<AdminLog><LoginLog /></AdminLog>} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
        </Suspense>
        <GlobalDesktopDock/>
        </GamificationProvider></ShellStatusProvider>
      </LiquidGoldProvider></BrowserRouter>
    </AuthProvider>
  );
}

export default App;







